import { Link, useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

function CompactActionButton({ action }) {
  const navigate = useNavigate();
  const busy = Boolean(action.loading || action.disabled);
  const Icon = action.icon;
  const handleClick = () => {
    if (busy) return;
    if (action.action) {
      action.action();
      return;
    }
    if (action.path) navigate(action.path);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      title={action.label}
      aria-label={action.label}
      aria-busy={action.loading ? "true" : undefined}
      className={clsx(
        "grid h-10 w-10 flex-shrink-0 place-items-center rounded-[10px]",
        action.compactPrimary
          ? "bg-gradient-to-br from-[#2b6ef0] to-[#1d4fb0] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_1px_2px_rgba(20,23,28,0.06)]"
          : "border border-[#d7dde6] bg-white text-[#334155]",
        busy && "cursor-not-allowed opacity-50"
      )}
    >
      {action.loading ? (
        <svg
          className="h-5 w-5 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : Icon ? (
        <Icon className="h-5 w-5" aria-hidden="true" />
      ) : null}
    </button>
  );
}

/**
 * Tablet/phone checklist chrome — same idea as Tegninger compact banner
 * (~40–44px), with a bit more info: back, title, progress, status.
 * Optional icon actions (edit / export) for HSE show pages — same bar, no third header.
 */
export default function ChecklistCompactPageHeader({
  title,
  subtitle,
  backTo,
  progressLabel,
  statusLabel,
  actions = [],
  className,
}) {
  const { t } = useTranslation();
  const visibleActions = (actions || []).filter(
    (action) => action && action.hidden !== true && !action.component
  );

  return (
    <header
      className={clsx(
        "z-30 flex min-h-11 min-w-0 max-w-full flex-shrink-0 items-center gap-1.5 overflow-hidden border-b border-[#e5e8ec] bg-white px-1.5 py-1 xl:hidden",
        className
      )}
    >
      {backTo ? (
        <Link
          to={backTo}
          className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-[10px] bg-gradient-to-br from-[#2b6ef0] to-[#1d4fb0] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_1px_2px_rgba(20,23,28,0.06)]"
          title={t("Back")}
          aria-label={t("Back")}
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
      ) : null}

      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="truncate text-[13px] font-semibold tracking-[-0.01em] text-[#14171c]">
          {title}
        </div>
        {subtitle ? (
          <div className="truncate text-[10px] font-medium text-[#59616d]">
            {subtitle}
          </div>
        ) : null}
      </div>

      {(progressLabel || statusLabel) && (
        <div className="flex max-w-[46%] shrink-0 flex-col items-end gap-0.5">
          {progressLabel ? (
            <span className="max-w-full truncate rounded-full bg-[#eaf1fe] px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-[#1d4fb0] ring-1 ring-[#c9d6fa]">
              {progressLabel}
            </span>
          ) : null}
          {statusLabel ? (
            <span className="max-w-full truncate text-[10px] font-semibold uppercase tracking-wide text-[#59616d]">
              {statusLabel}
            </span>
          ) : null}
        </div>
      )}

      {visibleActions.length > 0 ? (
        <div className="flex shrink-0 items-center gap-1">
          {visibleActions.map((action, index) => (
            <CompactActionButton
              key={action.key || action.label || index}
              action={action}
            />
          ))}
        </div>
      ) : null}
    </header>
  );
}
